import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMediaPredicate } from 'react-media-hook';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { getSecondsRemaining } from 'lib/utils';
import useLogout from 'hooks/useLogout';
import { refreshToken, signOut } from 'services/auth/api';
import useAuthStore from 'store/auth';

export default function SessionExpiration() {
  const { t } = useTranslation();
  const { onLogout, isLoggingOut } = useLogout();

  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  const setTokenGeneratedTime = useAuthStore(
    (store) => store.setTokenGeneratedTime
  );
  const setTokenExpiryTime = useAuthStore((store) => store.setTokenExpiryTime);

  const [open, setOpen] = useState<boolean>(false);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(
    getSecondsRemaining()
  );
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  useEffect(() => {
    const secRem = getSecondsRemaining();
    if (secRem < 126 && secRem > 0) setOpen(true);
  }, []);

  useEffect(() => {
    let allSecondsRemaining = setInterval(() => {
      setSecondsRemaining(getSecondsRemaining());

      if (secondsRemaining === 126) setOpen(true);

      if (secondsRemaining <= 6) {
        setOpen(false);
        onLogout();
        clearInterval(allSecondsRemaining);
      }
    }, 1000);

    return () => {
      clearInterval(allSecondsRemaining);
    };
  }, [secondsRemaining]);

  const onContinueSession = async () => {
    setIsRefreshing(true);
    try {
      const res = await refreshToken();
      setTokenGeneratedTime(res.generation_time);
      setTokenExpiryTime(res.expiry_time);
    } catch (error) {
      await signOut();
    } finally {
      setIsRefreshing(false);
      setOpen(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className='max-w-md px-4 py-6 sm:px-8 sm:py-12'
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        hideClose
      >
        <DialogHeader className='flex flex-col items-center justify-center w-full gap-y-6'>
          <DialogTitle>{t('session_expiration_heading')}</DialogTitle>
          <DialogDescription className='text-base font-medium'>
            {t('logged_out_in')}{' '}
            <span className='font-semibold'>{secondsRemaining - 5}</span>{' '}
            {t('seconds')}
          </DialogDescription>
        </DialogHeader>

        <div className='mt-6'>
          <div className='flex justify-center gap-x-4'>
            <Button
              variant='outline'
              size='sm'
              className='px-8 border-primary text-primary hover:bg-primary/10'
              onClick={onLogout}
              disabled={isLoggingOut || isRefreshing}
            >
              {isLoggingOut && (
                <Loader2 className='mr-2 size-4 animate-spin' role='status' />
              )}
              {t('logout')}
            </Button>
            <Button
              size='sm'
              className='px-8'
              disabled={isLoggingOut || isRefreshing}
              onClick={onContinueSession}
            >
              {isRefreshing && (
                <Loader2 className='mr-2 size-4 animate-spin' role='status' />
              )}
              {t(isMediaMobile ? 'continue' : 'continue_session')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
